import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { CheckCircle, Phone, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BOUTIQUES = ['martigny', 'verbier', 'sion'];
const CARD_OPTIONS = ['none', 'small', 'a6'];
const DELIVERY_OPTIONS = ['yes', 'no', 'pickup'];
const PAYMENT_OPTIONS = ['email', 'postal', 'card'];

const Orders = () => {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    boutique: '',
    description: '',
    budget: '',
    greeting_card: 'none',
    greeting_card_text: '',
    delivery: '',
    delivery_date: '',
    recipient_name: '',
    recipient_firstname: '',
    sender_phone: '',
    recipient_phone: '',
    delivery_address: '',
    payment: '',
    email: '',
    billing_address: '',
  });

  useEffect(() => { document.title = `${t('orders.title')} — Les Noces de Ginkgo`; }, [t]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const validate = () => {
    if (!form.boutique) return t('orders.boutique_label').replace(' *', '') + ' requis';
    if (!form.description) return t('orders.description_label').replace(' *', '') + ' requis';
    if (!form.budget) return t('orders.budget_label').replace(' *', '').split('(')[0].trim() + ' requis';
    if (!form.greeting_card) return t('orders.card_label').replace(' *', '') + ' requis';
    if (form.greeting_card !== 'none' && !form.greeting_card_text) return t('orders.card_text_label').replace(' *', '') + ' requis';
    if (!form.delivery) return t('orders.delivery_label').replace(' *', '') + ' requis';
    if (form.delivery === 'yes' && !form.delivery_date) return t('orders.delivery_date_label').replace(' *', '') + ' requis';
    if (!form.sender_phone) return t('orders.sender_phone').replace(' *', '') + ' requis';
    if (form.delivery === 'yes' && !form.recipient_name) return 'Nom destinataire requis';
    if (form.delivery === 'yes' && !form.delivery_address) return 'Adresse de livraison requise';
    if (!form.payment) return t('orders.payment_label').replace(' *', '') + ' requis';
    if (form.payment === 'email' && !form.email) return 'Email requis';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) { setError(validationError); return; }
    setLoading(true);
    try {
      const payload = {
        boutique: form.boutique,
        description: form.description,
        budget: form.budget,
        greeting_card: form.greeting_card,
        greeting_card_text: form.greeting_card !== 'none' ? form.greeting_card_text : null,
        delivery: form.delivery,
        delivery_date: form.delivery === 'yes' ? form.delivery_date : null,
        recipient_name: form.delivery === 'yes' ? form.recipient_name : null,
        recipient_firstname: form.delivery === 'yes' ? form.recipient_firstname : null,
        sender_phone: form.sender_phone,
        recipient_phone: form.delivery === 'yes' ? form.recipient_phone : null,
        delivery_address: form.delivery === 'yes' ? form.delivery_address : null,
        payment: form.payment,
        email: form.email || null,
        billing_address: form.billing_address || null,
        language: localStorage.getItem('ginkgo_lang') || 'fr',
      };
      await axios.post(`${API}/orders`, payload);
      setSubmitted(true);
    } catch (err) {
      setError('Une erreur est survenue. Veuillez réessayer ou nous appeler.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="font-sans bg-cream min-h-screen pt-16 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-lg w-full text-center bg-white border border-stone-100 p-10"
          data-testid="order-success"
        >
          <CheckCircle size={48} className="text-ginkgo-600 mx-auto mb-6" />
          <h2 className="font-serif text-3xl text-ginkgo-800 mb-4">{t('orders.success_title')}</h2>
          <p className="text-stone-600 text-base leading-relaxed mb-8">{t('orders.success_text')}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              data-testid="new-order-btn"
              onClick={() => { setSubmitted(false); setForm({ boutique: '', description: '', budget: '', greeting_card: 'none', greeting_card_text: '', delivery: '', delivery_date: '', recipient_name: '', recipient_firstname: '', sender_phone: '', recipient_phone: '', delivery_address: '', payment: '', email: '', billing_address: '' }); }}
              className="bg-ginkgo-800 text-white px-8 py-3 text-xs uppercase tracking-widest font-medium hover:bg-ginkgo-900 transition-colors"
            >
              {t('orders.new_order')}
            </button>
            <a
              href="tel:+41277222750"
              className="inline-flex items-center justify-center gap-2 border border-ginkgo-800 text-ginkgo-800 px-8 py-3 text-xs uppercase tracking-widest font-medium hover:bg-ginkgo-50 transition-colors"
            >
              <Phone size={12} /> Appeler
            </a>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="font-sans bg-cream min-h-screen pt-16">

      {/* Header */}
      <section className="bg-ginkgo-900 py-20 md:py-28 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <p className="text-gold text-xs uppercase tracking-[0.3em] font-medium mb-4">Martigny · Verbier · Sion</p>
          <h1 className="font-serif text-5xl md:text-6xl text-white font-medium">{t('orders.title')}</h1>
          <p className="mt-4 text-white/60 text-base max-w-xl mx-auto px-4">{t('orders.subtitle')}</p>
          <div className="mt-6 flex items-center justify-center gap-2 text-white/50 text-xs">
            <Phone size={12} />
            <span>{t('orders.call_note')}</span>
          </div>
        </motion.div>
      </section>

      {/* Form */}
      <div className="max-w-2xl mx-auto px-4 py-12 md:py-16">
        <p className="text-stone-400 text-xs mb-8">{t('orders.required')}</p>

        <form onSubmit={handleSubmit} data-testid="order-form" className="space-y-10">

          {/* Section: Order */}
          <section>
            <h2 className="font-serif text-2xl text-ginkgo-800 mb-6 pb-2 border-b border-stone-200">
              {t('orders.section_order')}
            </h2>
            <div className="space-y-5">
              <div>
                <label className="block text-xs uppercase tracking-widest text-stone-600 font-medium mb-2">
                  {t('orders.boutique_label')}
                </label>
                <select
                  name="boutique"
                  value={form.boutique}
                  onChange={handleChange}
                  data-testid="order-boutique"
                  className="w-full border border-stone-200 bg-white px-4 h-12 text-sm text-stone-700 focus:outline-none focus:border-ginkgo-500 rounded-none"
                >
                  <option value="">{t('orders.boutique_placeholder')}</option>
                  {BOUTIQUES.map(b => (
                    <option key={b} value={b}>{t(`orders.boutique_${b}`)}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-widest text-stone-600 font-medium mb-2">
                  {t('orders.description_label')}
                </label>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  rows={5}
                  placeholder={t('orders.description_placeholder')}
                  data-testid="order-description"
                  className="w-full border border-stone-200 bg-white px-4 py-3 text-sm text-stone-700 focus:outline-none focus:border-ginkgo-500 rounded-none resize-none"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-widest text-stone-600 font-medium mb-2">
                  {t('orders.budget_label')}
                </label>
                <input
                  type="text"
                  name="budget"
                  value={form.budget}
                  onChange={handleChange}
                  placeholder={t('orders.budget_placeholder')}
                  data-testid="order-budget"
                  className="w-full border border-stone-200 bg-white px-4 h-12 text-sm text-stone-700 focus:outline-none focus:border-ginkgo-500 rounded-none"
                />
              </div>
            </div>
          </section>

          {/* Section: Card */}
          <section>
            <h2 className="font-serif text-2xl text-ginkgo-800 mb-6 pb-2 border-b border-stone-200">
              {t('orders.section_card')}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-widest text-stone-600 font-medium mb-3">
                  {t('orders.card_label')}
                </label>
                <div className="space-y-2">
                  {CARD_OPTIONS.map(opt => (
                    <label key={opt} className="flex items-start gap-3 cursor-pointer group">
                      <input
                        type="radio"
                        name="greeting_card"
                        value={opt}
                        checked={form.greeting_card === opt}
                        onChange={handleChange}
                        data-testid={`order-card-${opt}`}
                        className="mt-0.5 accent-ginkgo-800"
                      />
                      <span className="text-sm text-stone-600 group-hover:text-ginkgo-800 transition-colors">
                        {t(`orders.card_${opt}`)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              {form.greeting_card !== 'none' && (
                <div>
                  <label className="block text-xs uppercase tracking-widest text-stone-600 font-medium mb-2">
                    {t('orders.card_text_label')}
                  </label>
                  <textarea
                    name="greeting_card_text"
                    value={form.greeting_card_text}
                    onChange={handleChange}
                    rows={3}
                    placeholder={t('orders.card_text_placeholder')}
                    data-testid="order-card-text"
                    maxLength={form.greeting_card === 'small' ? 120 : 500}
                    className="w-full border border-stone-200 bg-white px-4 py-3 text-sm text-stone-700 focus:outline-none focus:border-ginkgo-500 rounded-none resize-none"
                  />
                  {form.greeting_card === 'small' && (
                    <p className="text-xs text-stone-400 mt-1">{form.greeting_card_text.length}/120 caractères</p>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* Section: Delivery */}
          <section>
            <h2 className="font-serif text-2xl text-ginkgo-800 mb-6 pb-2 border-b border-stone-200">
              {t('orders.section_delivery')}
            </h2>
            <div className="space-y-5">
              <div>
                <label className="block text-xs uppercase tracking-widest text-stone-600 font-medium mb-3">
                  {t('orders.delivery_label')}
                </label>
                <div className="space-y-2">
                  {DELIVERY_OPTIONS.map(opt => (
                    <label key={opt} className="flex items-center gap-3 cursor-pointer group">
                      <input
                        type="radio"
                        name="delivery"
                        value={opt}
                        checked={form.delivery === opt}
                        onChange={handleChange}
                        data-testid={`order-delivery-${opt}`}
                        className="accent-ginkgo-800"
                      />
                      <span className="text-sm text-stone-600 group-hover:text-ginkgo-800 transition-colors">
                        {t(`orders.delivery_${opt}`)}
                      </span>
                    </label>
                  ))}
                </div>
                {form.boutique === 'sion' && form.delivery === 'yes' && (
                  <p className="text-xs text-gold mt-2 font-medium">{t('orders.delivery_sion_note')}</p>
                )}
              </div>

              {form.delivery === 'yes' && (
                <div>
                  <label className="block text-xs uppercase tracking-widest text-stone-600 font-medium mb-2">
                    {t('orders.delivery_date_label')}
                  </label>
                  <input
                    type="text"
                    name="delivery_date"
                    value={form.delivery_date}
                    onChange={handleChange}
                    placeholder={t('orders.delivery_date_placeholder')}
                    data-testid="order-delivery-date"
                    className="w-full border border-stone-200 bg-white px-4 h-12 text-sm text-stone-700 focus:outline-none focus:border-ginkgo-500 rounded-none"
                  />
                  <p className="text-xs text-stone-400 mt-1">{t('orders.delivery_note')}</p>
                </div>
              )}
            </div>
          </section>

          {/* Section: Recipient (only if delivery = yes) */}
          {form.delivery === 'yes' && (
            <section>
              <h2 className="font-serif text-2xl text-ginkgo-800 mb-6 pb-2 border-b border-stone-200">
                {t('orders.section_recipient')}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-stone-600 font-medium mb-2">
                    {t('orders.recipient_name')}
                  </label>
                  <input
                    type="text"
                    name="recipient_name"
                    value={form.recipient_name}
                    onChange={handleChange}
                    data-testid="order-recipient-name"
                    className="w-full border border-stone-200 bg-white px-4 h-12 text-sm text-stone-700 focus:outline-none focus:border-ginkgo-500 rounded-none"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-stone-600 font-medium mb-2">
                    {t('orders.recipient_firstname')}
                  </label>
                  <input
                    type="text"
                    name="recipient_firstname"
                    value={form.recipient_firstname}
                    onChange={handleChange}
                    data-testid="order-recipient-firstname"
                    className="w-full border border-stone-200 bg-white px-4 h-12 text-sm text-stone-700 focus:outline-none focus:border-ginkgo-500 rounded-none"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-stone-600 font-medium mb-2">
                    {t('orders.sender_phone')}
                  </label>
                  <input
                    type="tel"
                    name="sender_phone"
                    value={form.sender_phone}
                    onChange={handleChange}
                    data-testid="order-sender-phone"
                    className="w-full border border-stone-200 bg-white px-4 h-12 text-sm text-stone-700 focus:outline-none focus:border-ginkgo-500 rounded-none"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-stone-600 font-medium mb-2">
                    {t('orders.recipient_phone')}
                  </label>
                  <input
                    type="tel"
                    name="recipient_phone"
                    value={form.recipient_phone}
                    onChange={handleChange}
                    data-testid="order-recipient-phone"
                    className="w-full border border-stone-200 bg-white px-4 h-12 text-sm text-stone-700 focus:outline-none focus:border-ginkgo-500 rounded-none"
                  />
                  <p className="text-xs text-stone-400 mt-1">{t('orders.recipient_phone_note')}</p>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs uppercase tracking-widest text-stone-600 font-medium mb-2">
                    {t('orders.delivery_address')}
                  </label>
                  <input
                    type="text"
                    name="delivery_address"
                    value={form.delivery_address}
                    onChange={handleChange}
                    data-testid="order-delivery-address"
                    className="w-full border border-stone-200 bg-white px-4 h-12 text-sm text-stone-700 focus:outline-none focus:border-ginkgo-500 rounded-none"
                  />
                </div>
              </div>
            </section>
          )}

          {/* Sender phone when no delivery */}
          {(form.delivery === 'no' || form.delivery === 'pickup') && (
            <section>
              <h2 className="font-serif text-2xl text-ginkgo-800 mb-6 pb-2 border-b border-stone-200">
                Vos coordonnées
              </h2>
              <div>
                <label className="block text-xs uppercase tracking-widest text-stone-600 font-medium mb-2">
                  {t('orders.sender_phone')}
                </label>
                <input
                  type="tel"
                  name="sender_phone"
                  value={form.sender_phone}
                  onChange={handleChange}
                  data-testid="order-sender-phone-alt"
                  className="w-full border border-stone-200 bg-white px-4 h-12 text-sm text-stone-700 focus:outline-none focus:border-ginkgo-500 rounded-none"
                />
              </div>
            </section>
          )}

          {/* Section: Payment */}
          {form.delivery !== '' && (
            <section>
              <h2 className="font-serif text-2xl text-ginkgo-800 mb-6 pb-2 border-b border-stone-200">
                {t('orders.section_payment')}
              </h2>
              <div className="space-y-5">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-stone-600 font-medium mb-3">
                    {t('orders.payment_label')}
                  </label>
                  <div className="space-y-2">
                    {PAYMENT_OPTIONS.map(opt => (
                      <label key={opt} className="flex items-center gap-3 cursor-pointer group">
                        <input
                          type="radio"
                          name="payment"
                          value={opt}
                          checked={form.payment === opt}
                          onChange={handleChange}
                          data-testid={`order-payment-${opt}`}
                          className="accent-ginkgo-800"
                        />
                        <span className="text-sm text-stone-600 group-hover:text-ginkgo-800 transition-colors">
                          {t(`orders.payment_${opt}`)}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {form.payment === 'email' && (
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-stone-600 font-medium mb-2">
                      {t('orders.email_label')}
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder={t('orders.email_placeholder')}
                      data-testid="order-email"
                      className="w-full border border-stone-200 bg-white px-4 h-12 text-sm text-stone-700 focus:outline-none focus:border-ginkgo-500 rounded-none"
                    />
                  </div>
                )}

                {(form.payment === 'email' || form.payment === 'postal') && (
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-stone-600 font-medium mb-2">
                      {t('orders.billing_address_label')}
                    </label>
                    <input
                      type="text"
                      name="billing_address"
                      value={form.billing_address}
                      onChange={handleChange}
                      data-testid="order-billing-address"
                      className="w-full border border-stone-200 bg-white px-4 h-12 text-sm text-stone-700 focus:outline-none focus:border-ginkgo-500 rounded-none"
                    />
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Error message */}
          {error && (
            <div data-testid="order-error" className="flex items-start gap-3 bg-red-50 border border-red-200 px-4 py-3">
              <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            data-testid="order-submit"
            className="w-full bg-ginkgo-800 text-white py-4 text-sm uppercase tracking-widest font-medium hover:bg-ginkgo-900 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? t('orders.submitting') : t('orders.submit')}
          </button>

          <p className="text-center text-xs text-stone-400">
            {t('orders.call_note')}
          </p>
        </form>
      </div>
    </div>
  );
};

export default Orders;
